import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import SiteVisitForm from "@/components/SiteVisitForm";

const SECTIONS = [
  {
    title: "1. What information we collect",
    body: (
      <>
        <p className="text-zinc-300">We may collect personal and business information such as:</p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Full name</li>
          <li>Company name</li>
          <li>Job title / position</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Business or site address</li>
          <li>Number of employees or people on site</li>
          <li>Preferred site visit date and time</li>
          <li>Information you submit in forms or enquiries</li>
          <li>Website usage data (cookies/analytics)</li>
        </ul>
        <p className="mt-3 text-zinc-300">We collect only what is reasonably necessary to provide our services.</p>
      </>
    ),
  },
  {
    title: "2. How we collect information",
    body: (
      <>
        <p className="text-zinc-300">We collect information when you:</p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Submit a form on our website</li>
          <li>Request a free site visit</li>
          <li>Contact us by phone or email</li>
          <li>Use our website (cookies & analytics)</li>
        </ul>
      </>
    ),
  },
  {
    title: "3. How we use your information",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Assess eligibility for free vending machine installation</li>
          <li>Contact you about your enquiry or site visit</li>
          <li>Arrange inspections, installation and service</li>
          <li>Provide customer support</li>
          <li>Improve our website and service quality</li>
          <li>Meet legal and operational obligations</li>
        </ul>
        <p className="mt-3 text-zinc-300"><b className="text-white">We do not sell or rent</b> personal information.</p>
      </>
    ),
  },
  {
    title: "4. Disclosure to third parties",
    body: (
      <>
        <p className="text-zinc-300">Your information may be shared only when necessary with:</p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Our staff and authorised contractors</li>
          <li>Service partners who assist with site visits/installation</li>
          <li>Technology providers (hosting, forms, analytics)</li>
        </ul>
        <p className="mt-3 text-zinc-300">We require third parties to handle information securely and confidentially.</p>
      </>
    ),
  },
  {
    title: "5. Cookies & analytics",
    body: <p className="text-zinc-300">We may use cookies and analytics tools to understand how visitors use our website, improve performance, and measure marketing effectiveness. You can disable cookies in your browser settings.</p>,
  },
  {
    title: "6. Storage & security",
    body: <p className="text-zinc-300">We take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification or disclosure.</p>,
  },
  {
    title: "7. Accessing or correcting your information",
    body: <p className="text-zinc-300">You may request access to, or correction of, your personal information by contacting us. We will respond within a reasonable timeframe.</p>,
  },
  {
    title: "8. Marketing communications",
    body: <p className="text-zinc-300">If you receive communications from us, they relate to our services. You can opt out at any time by contacting us.</p>,
  },
  {
    title: "9. External links",
    body: <p className="text-zinc-300">Our website may link to third-party sites. We are not responsible for their privacy practices. Please review their privacy policies.</p>,
  },
  {
    title: "10. Changes to this policy",
    body: <p className="text-zinc-300">We may update this policy from time to time. Any updates will be posted on this page with a revised "Last updated" date.</p>,
  },
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="text-white">
      <Seo />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-radial-fade" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
              Anytime Vending Machines
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Privacy Policy
            </h1>
            <p className="mt-5 text-base text-zinc-300 max-w-2xl">
              This Privacy Policy explains how we collect, use, store and protect personal information when you visit our website, submit forms, or engage with our services.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#policy-body"
                data-testid="privacy-read-button"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Read The Policy
              </a>
              <Link
                to="/"
                data-testid="privacy-home-button"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
              >
                Back To Home
              </Link>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
              {["We never sell your data", "Used only for service & support", "Stored securely"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#FE7200]" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FE7200]">Policy snapshot</div>
              <h3 className="mt-2 font-display text-xl font-bold text-white">Quick reference details</h3>
              <ul className="mt-4 space-y-2 text-sm text-zinc-300">
                <li><b className="text-white">Last updated:</b> January 2026</li>
                <li><b className="text-white">Applies to:</b> website, calls, emails & forms</li>
                <li><b className="text-white">Regions:</b> Australia, United Kingdom, USA, Canada</li>
              </ul>
              <p className="mt-4 text-sm text-zinc-300">
                If you have questions, contact us at <a href="mailto:info.nector@gmail.com" className="text-[#FE7200] hover:underline">info.nector@gmail.com</a>.
              </p>
              <Link
                to="/"
                data-testid="privacy-return-home"
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Return To Home
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section id="policy-body" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-16" data-testid="policy-sections">
        <h2 className="font-display text-3xl font-bold tracking-tight">Privacy Policy</h2>
        <p className="mt-3 text-zinc-300">We are committed to protecting your privacy and handling personal information responsibly and transparently.</p>

        <div className="mt-10 space-y-6">
          {SECTIONS.map((s, i) => (
            <section
              key={i}
              data-testid={`policy-section-${i + 1}`}
              className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7"
            >
              <h3 className="font-display text-xl font-bold text-white">{s.title}</h3>
              <div className="mt-3 text-sm leading-relaxed">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7">
            <h3 className="font-display text-xl font-bold text-white">Back to Anytime Vending Machines</h3>
            <p className="mt-2 text-sm text-zinc-300">Ready to book a free site visit for a workplace with 30+ staff or steady foot traffic?</p>
            <ul className="mt-4 text-sm text-zinc-300 list-disc pl-5 space-y-1.5">
              <li>Modern machines</li>
              <li>Tap &amp; Go + cash/coin</li>
              <li>Fully stocked &amp; serviced</li>
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/" data-testid="privacy-back-home" className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors">
                Back To Home
              </Link>
              <a href="#policy-body" data-testid="privacy-back-top" className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-white/10 transition-colors">
                Back To Top
              </a>
            </div>
            <p className="mt-4 text-xs text-zinc-500">Contact: info.nector@gmail.com</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7">
            <h3 className="font-display text-xl font-bold text-white">Quick summary</h3>
            <dl className="mt-4 space-y-3">
              {[
                ["We collect", "Only what we need"],
                ["We use it for", "Service & support"],
                ["We never", "Sell your data"],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-white/10 bg-[#0f0f10] px-4 py-3">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{k}</dt>
                  <dd className="text-sm text-white">{v}</dd>
                </div>
              ))}
            </dl>
            <Link to="/" data-testid="privacy-summary-home" className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors">
              Return To Home
            </Link>
          </div>
        </div>
      </section>

      <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 pb-20" data-testid="privacy-form-section">
        <SiteVisitForm source="privacy-page" testIdPrefix="privacy-booking" />
      </section>
    </div>
  );
}
